import React from "react";
import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";

const testimonials = [
  {
    quote:
      "Booked Tuesday, moved Saturday. The driver was vetted, the price was locked, no awkward cash handoff. This is how moving should feel in 2026.",
    name: "Aisha M.",
    role: "House move · 2-bed flat",
    city: "Leeds",
    initials: "AM",
    swatch: "#1E4D55",
  },
  {
    quote:
      "I cleared £980 last Saturday across three jobs. Payout hit my bank by Friday lunchtime. No invoicing, no chasing — finally the right side of the deal.",
    name: "Daniel R.",
    role: "Self-employed Luton driver",
    city: "Manchester",
    initials: "DR",
    swatch: "#5BB247",
  },
  {
    quote:
      "We had 14 desks to relocate across two floors. The team showed up exactly when promised, escrow released the same evening. We'll be using Raanah for every office move from here on.",
    name: "Priya K.",
    role: "Operations Lead, Northhill Ltd",
    city: "Birmingham",
    initials: "PK",
    swatch: "#0A0A0A",
  },
];

export default function Testimonials() {
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="bg-[#F5F5F5] border-y border-[#0A0A0A]/5"
    >
      <div className="px-6 md:px-12 lg:px-24 py-20 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
              From the pilot
            </p>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl tracking-tighter font-black text-[#0A0A0A] leading-[1]">
              Early customers
              <br />
              &amp; early earners.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#475569] leading-relaxed">
              A few words from the first cohort using Raanah for real jobs
              across Leeds, Manchester and Birmingham.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-[#0A0A0A]/10">
          {testimonials.map((t, i) => (
            <motion.figure
              key={t.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: "easeOut" }}
              data-testid={`testimonial-card-${i}`}
              className="bg-white border-r border-b border-[#0A0A0A]/10 p-8 md:p-10 flex flex-col"
            >
              <Quote size={28} className="text-[#5BB247]" strokeWidth={2.5} />
              <blockquote className="mt-6 text-lg leading-relaxed text-[#0A0A0A]">
                &ldquo;{t.quote}&rdquo;
              </blockquote>

              <div className="mt-auto pt-8 flex items-center gap-4">
                <div
                  aria-hidden="true"
                  className="w-11 h-11 flex items-center justify-center font-display font-bold text-white text-sm tracking-tight"
                  style={{ backgroundColor: t.swatch }}
                >
                  {t.initials}
                </div>
                <figcaption className="flex-1 min-w-0">
                  <p className="font-display font-bold text-[#0A0A0A] tracking-tight truncate">
                    {t.name}
                  </p>
                  <p className="text-xs text-[#475569] truncate">
                    {t.role} · {t.city}
                  </p>
                </figcaption>
                <div className="flex items-center gap-0.5" aria-label="5 out of 5 stars">
                  {[...Array(5)].map((_, idx) => (
                    <Star
                      key={idx}
                      size={12}
                      className="text-[#F5A623] fill-[#F5A623]"
                    />
                  ))}
                </div>
              </div>
            </motion.figure>
          ))}
        </div>

        {/* Disclaimer for pilot quotes */}
        <p className="mt-6 text-xs text-[#475569]" data-testid="testimonials-disclaimer">
          * Names and details from pilot participants, lightly edited for clarity.
          Anonymised by request. Real quotes from real jobs.
        </p>
      </div>
    </section>
  );
}
